import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { View, FlatList, Pressable, StyleSheet, Platform, Alert, Dimensions, TextInput, TouchableOpacity, ActivityIndicator, Modal, ScrollView } from 'react-native';
import { Text, Title, Card } from '@constants/Themed';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons, AntDesign, Feather } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import { useDispatch } from 'react-redux';
import { SHOW_LOADING_MODAL, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { YStack, XStack, H4, Paragraph, Image, Button, Input } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';

// Hero Button Styles
const HERO_PRIMARY_COLOR = '#5CC0EB'; // Sky Blue from Hero.tsx
const HERO_TEXT_COLOR = '#FFFFFF';

const getHeroButtonStyle = (theme: boolean, variant: 'solid' | 'outline' = 'solid') => {
  if (variant === 'solid') {
    return {
      backgroundColor: HERO_PRIMARY_COLOR,
      shadowColor: HERO_PRIMARY_COLOR,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 6,
      borderWidth: 0,
    };
  } else {
    return {
      backgroundColor: theme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 41, 59, 0.5)',
      borderWidth: 2,
      borderColor: 'rgba(92, 192, 235, 0.3)',
    };
  }
};

interface FolderItem {
  id: string;
  name: string;
  createdAt: string;
  parentId?: string | null;
  documentCount?: number;
}

interface DocumentItem {
  id: string;
  name: string;
  type: string;
  size?: number;
  uploadedAt: string;
  url?: string;
  title?: string; // AI-generated title
  description?: string; // AI-generated description
  summary?: string; // AI-generated summary
  thumbnailUrl?: string; // Optional thumbnail/preview image
  folderId?: string | null; // Folder this document belongs to
}

type ListItem = DocumentItem | FolderItem;

const isFolder = (item: ListItem): item is FolderItem => {
  return 'createdAt' in item && !('type' in item);
};

const UserUploadedDocumentsScreen = ({ navigation }) => {
  const theme = themeSelector();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [screenData, setScreenData] = useState(() => Dimensions.get('window'));
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<FolderItem[]>([]);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [documentToMove, setDocumentToMove] = useState<DocumentItem | null>(null);

  const { data: documents, isLoading, refetch } = useQuery({
    queryKey: ['userUploadedDocuments', currentFolderId],
    queryFn: async () => {
      try {
        const response = await apis.getUserUploadedDocuments(currentFolderId);
        return response?.data || [];
      } catch (error) {
        console.error('Error fetching documents:', error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: folders, isLoading: foldersLoading } = useQuery({
    queryKey: ['userDocumentFolders', currentFolderId],
    queryFn: async () => {
      try {
        const response = await apis.getDocumentFolders(currentFolderId);
        return response?.data || [];
      } catch (error) {
        console.error('Error fetching folders:', error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      return await apis.createDocumentFolder(name, currentFolderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userDocumentFolders'] });
      setShowCreateFolderModal(false);
      setNewFolderName('');
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Folder created successfully', type: 'success' }
      });
    },
    onError: (error) => {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to create folder', type: 'error' }
      });
      console.error('Error creating folder:', error);
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (folderId: string) => {
      return await apis.deleteDocumentFolder(folderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userDocumentFolders'] });
      queryClient.invalidateQueries({ queryKey: ['userUploadedDocuments'] });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Folder deleted successfully', type: 'success' }
      });
    },
    onError: (error) => {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to delete folder', type: 'error' }
      });
      console.error('Error deleting folder:', error);
    },
  });

  const moveDocumentMutation = useMutation({
    mutationFn: async ({ documentId, folderId }: { documentId: string; folderId: string | null }) => {
      return await apis.moveDocumentToFolder(documentId, folderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userUploadedDocuments'] });
      queryClient.invalidateQueries({ queryKey: ['userDocumentFolders'] });
      setShowMoveModal(false);
      setDocumentToMove(null);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Document moved successfully', type: 'success' }
      });
    },
    onError: (error) => {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to move document', type: 'error' }
      });
      console.error('Error moving document:', error);
    },
  });

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handleUploadDocument = useCallback(async () => {
    dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Selecting document...' } });

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          // PDFs
          'application/pdf',
          // Microsoft Word
          'application/msword', // .doc
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
          // Microsoft Excel
          'application/vnd.ms-excel', // .xls
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
          // Microsoft PowerPoint
          'application/vnd.ms-powerpoint', // .ppt
          'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
          // Text files
          'text/plain',
          'text/rtf',
          'text/csv',
          // Images (for document thumbnails/previews)
          'image/*',
          // Other common document types
          'application/rtf',
          'application/vnd.oasis.opendocument.text', // .odt
          'application/vnd.oasis.opendocument.spreadsheet', // .ods
          'application/vnd.oasis.opendocument.presentation', // .odp
          // Allow all file types as fallback
          '*/*'
        ],
        copyToCacheDirectory: true,
        multiple: false,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        dispatch({ type: HIDE_LOADING_MODAL });
        return;
      }

      const file = result.assets[0];

      // Read file as base64 for upload - handle different platforms and URI formats
      let base64Data: string;
      try {
        dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Reading file...' } });

        // Check if URI is already a data URI (base64)
        if (file.uri.startsWith('data:')) {
          base64Data = file.uri;
        } else if (file.uri.includes('base64,')) {
          // Handle base64 data URI format
          base64Data = file.uri;
        } else {
          // For file:// or content:// URIs, read using FileSystem or fetch
          if (Platform.OS === 'web') {
            // Web platform - use fetch
            const response = await fetch(file.uri);
            const blob = await response.blob();
            base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const result = reader.result as string;
                resolve(result);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } else {
            // Native platform - try FileSystem first, fallback to fetch
            try {
              // Ensure file is accessible
              const fileInfo = await FileSystem.getInfoAsync(file.uri);
              if (!fileInfo.exists) {
                throw new Error('File does not exist at URI');
              }

              // Read as base64
              base64Data = await FileSystem.readAsStringAsync(file.uri, {
                encoding: 'base64' as any,
              });

              // Add data URL prefix if not already present
              const mimeType = file.mimeType || 'application/octet-stream';
              if (!base64Data.startsWith('data:')) {
                base64Data = `data:${mimeType};base64,${base64Data}`;
              }
            } catch (fileSystemError) {
              console.log('FileSystem read failed, trying fetch:', fileSystemError);
              // Fallback to fetch for content:// URIs or other cases
              const response = await fetch(file.uri);
              const blob = await response.blob();
              base64Data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const result = reader.result as string;
                  resolve(result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            }
          }
        }

        // Ensure we have a valid base64 string
        if (!base64Data || base64Data.length === 0) {
          throw new Error('Failed to read file data');
        }
      } catch (readError) {
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: `Failed to read file: ${readError instanceof Error ? readError.message : 'Unknown error'}`,
            type: 'error'
          }
        });
        console.error('Error reading file:', readError);
        console.error('File URI:', file.uri);
        console.error('File name:', file.name);
        console.error('File size:', file.size);
        return;
      }

      // Determine MIME type - use file.mimeType or infer from extension
      let mimeType = file.mimeType;
      if (!mimeType || mimeType === 'application/octet-stream') {
        const extension = file.name.split('.').pop()?.toLowerCase();
        const mimeTypeMap: Record<string, string> = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'ppt': 'application/vnd.ms-powerpoint',
          'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'txt': 'text/plain',
          'rtf': 'application/rtf',
          'csv': 'text/csv',
        };
        mimeType = extension ? (mimeTypeMap[extension] || 'application/octet-stream') : 'application/octet-stream';
      }

      await apis.uploadUserDocument({
        name: file.name,
        type: mimeType,
        size: file.size,
        fileData: base64Data,
        folderId: currentFolderId,
      });

      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Document uploaded successfully', type: 'success' }
      });

      // Refresh the list
      await refetch();
    } catch (error) {
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to upload document', type: 'error' }
      });
      console.error('Error uploading document:', error);
    }
  }, [dispatch, refetch, currentFolderId]);

  const handleDeleteDocument = useCallback(async (documentId: string) => {
    Alert.alert(
      t('Delete Document'),
      t('Are you sure you want to delete this document?'),
      [
        { text: t('Cancel'), style: 'cancel' },
        {
          text: t('Delete'),
          style: 'destructive',
          onPress: async () => {
            dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Deleting document...' } });

            try {
              await apis.deleteUserDocument(documentId);

              dispatch({ type: HIDE_LOADING_MODAL });
              dispatch({
                type: SHOW_AUTH_MODAL,
                data: { content: 'Document deleted successfully', type: 'success' }
              });

              await refetch();
            } catch (error) {
              dispatch({ type: HIDE_LOADING_MODAL });
              dispatch({
                type: SHOW_AUTH_MODAL,
                data: { content: 'Failed to delete document', type: 'error' }
              });
              console.error('Error deleting document:', error);
            }
          },
        },
      ]
    );
  }, [dispatch, refetch, t]);

  const handleViewDocument = useCallback((document: DocumentItem) => {
    if (document.url) {
      // TODO: Implement document viewer or open in external app
      Alert.alert(t('View Document'), `Opening ${document.name}...`);
    }
  }, [t]);

  const handleCreateFolder = useCallback(() => {
    if (!newFolderName.trim()) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Please enter a folder name', type: 'error' }
      });
      return;
    }
    createFolderMutation.mutate(newFolderName.trim());
  }, [newFolderName, createFolderMutation, dispatch]);

  const handleDeleteFolder = useCallback(async (folderId: string) => {
    Alert.alert(
      t('Delete Folder'),
      t('Are you sure you want to delete this folder? All documents inside will be moved to the parent folder.'),
      [
        { text: t('Cancel'), style: 'cancel' },
        {
          text: t('Delete'),
          style: 'destructive',
          onPress: () => {
            deleteFolderMutation.mutate(folderId);
          },
        },
      ]
    );
  }, [deleteFolderMutation, t]);

  const handleOpenFolder = useCallback((folder: FolderItem) => {
    setCurrentFolderId(folder.id);
    setFolderPath(prev => [...prev, folder]);
  }, []);

  const handleNavigateToFolder = useCallback((folderId: string | null) => {
    if (folderId === null) {
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      const folderIndex = folderPath.findIndex(f => f.id === folderId);
      if (folderIndex !== -1) {
        setCurrentFolderId(folderId);
        setFolderPath(folderPath.slice(0, folderIndex + 1));
      }
    }
  }, [folderPath]);

  const handleMoveDocument = useCallback((document: DocumentItem) => {
    setDocumentToMove(document);
    setShowMoveModal(true);
  }, []);

  const handleMoveToFolder = useCallback((folderId: string | null) => {
    if (documentToMove) {
      moveDocumentMutation.mutate({ documentId: documentToMove.id, folderId });
    }
  }, [documentToMove, moveDocumentMutation]);

  const formatFileSize = useCallback((bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }, []);

  // Listen for orientation changes
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  // Always use single column for documents to show full metadata
  const numColumns = 1;

  // Helper function to get file extension from filename
  const getFileExtension = useCallback((filename: string) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }, []);

  const getFileIcon = useCallback((type: string, filename?: string) => {
    const lowerType = type?.toLowerCase() || '';
    const extension = filename ? getFileExtension(filename) : '';

    // PDF detection
    if (lowerType.includes('pdf') || extension === 'pdf') return 'pdf';

    // Microsoft Word detection (.doc, .docx)
    if (
      lowerType.includes('word') ||
      lowerType.includes('msword') ||
      lowerType.includes('wordprocessingml') ||
      extension === 'doc' ||
      extension === 'docx'
    ) return 'word';

    // Microsoft Excel detection (.xls, .xlsx)
    if (
      lowerType.includes('excel') ||
      lowerType.includes('spreadsheet') ||
      lowerType.includes('ms-excel') ||
      extension === 'xls' ||
      extension === 'xlsx' ||
      extension === 'csv'
    ) return 'excel';

    // Microsoft PowerPoint detection (.ppt, .pptx)
    if (
      lowerType.includes('powerpoint') ||
      lowerType.includes('presentation') ||
      lowerType.includes('ms-powerpoint') ||
      extension === 'ppt' ||
      extension === 'pptx'
    ) return 'powerpoint';

    // Text files
    if (
      lowerType.includes('text/plain') ||
      lowerType.includes('text/rtf') ||
      extension === 'txt' ||
      extension === 'rtf'
    ) return 'text';

    // Images
    if (lowerType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
      return 'image';
    }

    // Default file icon
    return 'file';
  }, [getFileExtension]);

  // Format date to relative time
  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
      }
      return `${diffInHours}h ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks}w ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  }, []);

  // Combine folders and documents into a single list
  const allItems = useMemo(() => {
    const items: ListItem[] = [];

    // Add folders first
    if (Array.isArray(folders)) {
      items.push(...folders);
    }

    // Add documents
    if (Array.isArray(documents)) {
      items.push(...documents);
    }

    return items;
  }, [folders, documents]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!Array.isArray(allItems)) return [];

    let items = allItems;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(item => {
        if (isFolder(item)) {
          return item.name.toLowerCase().includes(q);
        } else {
          const searchableText = [
            item.name,
            item.title,
            item.description,
            item.summary
          ].filter(Boolean).join(' ').toLowerCase();
          return searchableText.includes(q);
        }
      });
    }

    return items;
  }, [allItems, search]);

  // Folder item component
  const FolderItemComponent = React.memo(({ item, index }: { item: FolderItem; index: number }) => {
    const handlePress = useCallback(() => {
      handleOpenFolder(item);
    }, [item, handleOpenFolder]);

    return (
      <View style={stylesLocal.documentItemContainer}>
        <Card
          flex={1}
          onPress={handlePress}
          style={{
            backgroundColor: theme ? '#ffffff' : '#1e293b',
            borderColor: theme ? '#e2e8f0' : '#334155',
            borderWidth: 2,
            borderStyle: 'dashed',
            minHeight: 200,
          }}
        >
          <YStack flex={1} padding="$2" justifyContent="space-between">
            <YStack flex={1}>
              {/* Folder Icon */}
              <YStack style={stylesLocal.documentPlaceholder}>
                <MaterialIcons name="folder" size={64} color="#5CC0EB" />
              </YStack>

              {/* Content */}
              <YStack marginTop="$2" gap="$1.5" flex={1}>
                {/* Title */}
                <Text
                  fontSize="$3"
                  color={getModernTextColor(theme)}
                  fontWeight="600"
                  numberOfLines={2}
                >
                  {item.name}
                </Text>

                {/* Document Count */}
                <Text
                  fontSize="$2"
                  color={getModernTextColor(theme, 'muted')}
                  numberOfLines={1}
                >
                  {item.documentCount || 0} {item.documentCount === 1 ? 'document' : 'documents'}
                </Text>

                {/* Created Date */}
                {item.createdAt && (
                  <Text
                    fontSize="$1"
                    color={getModernTextColor(theme, 'muted')}
                    numberOfLines={1}
                    marginTop="$1"
                  >
                    Created {formatDate(item.createdAt)}
                  </Text>
                )}
              </YStack>
            </YStack>

            {/* Action buttons */}
            <XStack justifyContent="flex-end" gap="$2" marginTop="$2" paddingTop="$2" borderTopWidth={1} borderTopColor={theme ? '#e5e7eb' : '#374151'}>
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  handleOpenFolder(item);
                }}
                style={[
                  getHeroButtonStyle(theme, 'outline'),
                  {
                    padding: 8,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }
                ]}
              >
                <AntDesign name="folder-open" size={18} color={HERO_PRIMARY_COLOR} />
              </Pressable>
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  handleDeleteFolder(item.id);
                }}
                style={[
                  {
                    backgroundColor: theme ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.2)',
                    borderWidth: 1,
                    borderColor: 'rgba(239, 68, 68, 0.3)',
                    padding: 8,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }
                ]}
              >
                <AntDesign name="delete" size={18} color="#ef4444" />
              </Pressable>
            </XStack>
          </YStack>
        </Card>
      </View>
    );
  }, (prevProps, nextProps) => {
    return prevProps.item === nextProps.item && prevProps.index === nextProps.index;
  });

  // Document item component matching MediaScreen pattern
  const DocumentItemComponent = React.memo(({ item, index }: { item: DocumentItem; index: number }) => {
    const iconName = getFileIcon(item.type, item.name);
    const [imageLoadError, setImageLoadError] = useState(false);

    const title = item.title || item.name;
    const description = item.description || '';
    const summary = item.summary || '';

    // Build metadata string
    const metadata = useMemo(() => {
      const parts = [];
      if (item.size) parts.push(formatFileSize(item.size));
      if (item.uploadedAt) parts.push(formatDate(item.uploadedAt));
      return parts.join(' • ');
    }, [item.size, item.uploadedAt, formatFileSize, formatDate]);

    const handlePress = useCallback(() => {
      handleViewDocument(item);
    }, [item, handleViewDocument]);

    const handleImageError = useCallback(() => {
      setImageLoadError(true);
    }, []);

    return (
      <View style={stylesLocal.documentItemContainer}>
        <Card
          flex={1}
          onPress={handlePress}
          style={{
            backgroundColor: theme ? '#ffffff' : '#1e293b',
            borderColor: theme ? '#e2e8f0' : '#334155',
            overflow: 'hidden',
            minHeight: 200,
          }}
        >
          <XStack flex={1} padding="$3" gap="$3" alignItems="flex-start">
            {/* Thumbnail or Icon - Left side */}
            <YStack>
              {!imageLoadError && item.thumbnailUrl ? (
                <Image
                  source={{ uri: item.thumbnailUrl }}
                  style={stylesLocal.documentThumbnail}
                  resizeMode="cover"
                  onError={handleImageError}
                />
              ) : (
                <YStack style={stylesLocal.documentPlaceholder}>
                  {iconName === 'pdf' && <MaterialIcons name="picture-as-pdf" size={64} color="#dc2626" />}
                  {iconName === 'word' && <MaterialIcons name="description" size={64} color="#2563eb" />}
                  {iconName === 'excel' && <MaterialIcons name="table-chart" size={64} color="#16a34a" />}
                  {iconName === 'powerpoint' && <MaterialIcons name="slideshow" size={64} color="#f59e0b" />}
                  {iconName === 'text' && <MaterialIcons name="article" size={64} color="#64748b" />}
                  {iconName === 'image' && <MaterialIcons name="image" size={64} color="#7c3aed" />}
                  {iconName === 'file' && <Feather name="file" size={64} color="#6b7280" />}
                </YStack>
              )}
            </YStack>

            {/* Content - Right side, full width */}
            <YStack flex={1} gap="$2" justifyContent="space-between">
              <YStack gap="$2" flex={1}>
                {/* Title */}
                <Text
                  fontSize="$4"
                  color={getModernTextColor(theme)}
                  fontWeight="600"
                  numberOfLines={2}
                >
                  {title}
                </Text>

                {/* Description */}
                {description && (
                  <Text
                    fontSize="$3"
                    color={getModernTextColor(theme, 'secondary')}
                    numberOfLines={3}
                    lineHeight={20}
                  >
                    {description}
                  </Text>
                )}

                {/* Summary */}
                {summary && (
                  <Text
                    fontSize="$2"
                    color={getModernTextColor(theme, 'muted')}
                    numberOfLines={4}
                    lineHeight={18}
                  >
                    {summary}
                  </Text>
                )}

                {/* Metadata */}
                {metadata && (
                  <Text
                    fontSize="$2"
                    color={getModernTextColor(theme, 'muted')}
                    numberOfLines={1}
                    marginTop="$1"
                  >
                    {metadata}
                  </Text>
                )}
              </YStack>

              {/* Action buttons */}
              <XStack justifyContent="flex-end" gap="$2" marginTop="$2" paddingTop="$2" borderTopWidth={1} borderTopColor={theme ? '#e5e7eb' : '#374151'}>
                <Pressable
                  onPress={() => handleViewDocument(item)}
                  style={[
                    getHeroButtonStyle(theme, 'outline'),
                    {
                      padding: 8,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  ]}
                >
                  <AntDesign name="eye" size={18} color={HERO_PRIMARY_COLOR} />
                </Pressable>
                <Pressable
                  onPress={() => handleMoveDocument(item)}
                  style={[
                    getHeroButtonStyle(theme, 'outline'),
                    {
                      padding: 8,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  ]}
                >
                  <Feather name="move" size={18} color={HERO_PRIMARY_COLOR} />
                </Pressable>
                <Pressable
                  onPress={() => handleDeleteDocument(item.id)}
                  style={[
                    {
                      backgroundColor: theme ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.2)',
                      borderWidth: 1,
                      borderColor: 'rgba(239, 68, 68, 0.3)',
                      padding: 8,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  ]}
                >
                  <AntDesign name="delete" size={18} color="#ef4444" />
                </Pressable>
              </XStack>
            </YStack>
          </XStack>
        </Card>
      </View>
    );
  }, (prevProps, nextProps) => {
    return prevProps.item === nextProps.item && prevProps.index === nextProps.index;
  });

  // Render item for FlatList - single column layout
  const renderItem = useCallback(({ item, index }: { item: ListItem; index: number }) => {
    return (
      <View style={{
        width: '100%',
        paddingHorizontal: 8,
        marginBottom: 16,
      }}>
        {isFolder(item) ? (
          <FolderItemComponent item={item} index={index} />
        ) : (
          <DocumentItemComponent item={item} index={index} />
        )}
      </View>
    );
  }, []);

  return (
    <DefaultScreen backButton style={{ paddingRight: Platform.select({ web: 30, default: 0 }), paddingTop: 20, flex: 1, gap: 24 }} containerStyle={{ flex: 1, paddingTop: 20 }}>
      <Animatable.View
        animation="fadeInRight"
        duration={800}
        delay={200}
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 20,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 12 },
          shadowOpacity: theme ? 0.08 : 0.25,
          shadowRadius: 24,
          elevation: 12,
          borderWidth: 1,
          borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <View style={{
          padding: 24,
          borderBottomWidth: 1,
          borderBottomColor: theme ? '#e2e8f0' : '#334155'
        }}>
          {/* Breadcrumb Navigation */}
          {folderPath.length > 0 && (
            <XStack alignItems="center" gap={8} marginBottom={16} flexWrap="wrap">
              <Pressable
                onPress={() => handleNavigateToFolder(null)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6,
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                }}
              >
                <MaterialIcons name="home" size={16} color={getModernTextColor(theme)} />
                <Text style={{ color: getModernTextColor(theme), fontSize: 12, fontWeight: '500' }}>
                  {t('Home')}
                </Text>
              </Pressable>
              {folderPath.map((folder, index) => (
                <XStack key={folder.id} alignItems="center" gap={4}>
                  <MaterialIcons name="chevron-right" size={16} color={getModernTextColor(theme, 'muted')} />
                  <Pressable
                    onPress={() => handleNavigateToFolder(folder.id)}
                    style={{
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderRadius: 6,
                      backgroundColor: index === folderPath.length - 1
                        ? (theme ? '#e0f2fe' : '#1e3a5f')
                        : 'transparent',
                    }}
                  >
                    <Text style={{
                      color: getModernTextColor(theme),
                      fontSize: 12,
                      fontWeight: index === folderPath.length - 1 ? '600' : '400'
                    }}>
                      {folder.name}
                    </Text>
                  </Pressable>
                </XStack>
              ))}
            </XStack>
          )}

          <XStack flexWrap='wrap' gap={10} alignItems="center" justifyContent="space-between" marginBottom={16}>
            <YStack>
              <Title style={{ color: getModernTextColor(theme), fontSize: 24, fontWeight: '700' }}>
                {folderPath.length > 0 ? folderPath[folderPath.length - 1].name : t('Uploaded Documents')}
              </Title>
              <Text style={{
                color: getModernTextColor(theme, 'muted'),
                fontSize: 14,
                marginTop: 4
              }}>
                {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} • AI-powered summaries
              </Text>
            </YStack>
            <XStack gap={8}>
              <Pressable
                onPress={() => setShowCreateFolderModal(true)}
                style={[
                  getHeroButtonStyle(theme, 'outline'),
                  {
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }
                ]}
              >
                <MaterialIcons name="create-new-folder" size={16} color={HERO_PRIMARY_COLOR} />
                <Text style={{ color: HERO_PRIMARY_COLOR, fontWeight: '600', fontSize: 14 }}>
                  {t('New Folder')}
                </Text>
              </Pressable>
              <Pressable
                onPress={handleUploadDocument}
                style={[
                  getHeroButtonStyle(theme, 'solid'),
                  {
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }
                ]}
              >
                <Feather name="upload" size={16} color={HERO_TEXT_COLOR} />
                <Text style={{ color: HERO_TEXT_COLOR, fontWeight: '600', fontSize: 14 }}>
                  {t('Upload')}
                </Text>
              </Pressable>
            </XStack>
          </XStack>

          {/* Search Bar */}
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search documents..."
            placeholderTextColor={theme ? '#64748b' : '#94a3b8'}
            style={{
              backgroundColor: theme ? '#f8fafc' : '#334155',
              color: theme ? '#1e293b' : '#f1f5f9',
              borderRadius: 8,
              paddingHorizontal: 16,
              paddingVertical: 10,
              fontSize: 15,
              borderWidth: 1,
              borderColor: theme ? '#e2e8f0' : '#334155',
            }}
            clearButtonMode="while-editing"
          />
        </View>

        {/* Content */}
        <YStack flex={1} padding={24}>
          {/* Loading State */}
          {isLoading && (
            <YStack justifyContent="center" alignItems="center" flex={1} gap="$3">
              <ActivityIndicator
                size="large"
                color={HERO_PRIMARY_COLOR}
              />
              <Text color={getModernTextColor(theme, 'muted')} fontSize="$4" fontWeight="500">
                Loading documents...
              </Text>
            </YStack>
          )}

          {/* Empty State */}
          {!isLoading && !foldersLoading && filteredItems.length === 0 && (
            <YStack justifyContent="center" alignItems="center" flex={1} gap={16}>
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#334155',
                borderRadius: 16,
                padding: 24,
                alignItems: 'center'
              }}>
                <MaterialIcons
                  name={currentFolderId ? "folder-open" : "cloud-upload"}
                  size={48}
                  color={getModernTextColor(theme, 'muted')}
                />
                <Text style={{
                  color: getModernTextColor(theme),
                  fontSize: 16,
                  marginTop: 12,
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  {currentFolderId ? t('Folder is Empty') : t('No Documents Yet')}
                </Text>
                <Text style={{
                  color: getModernTextColor(theme, 'muted'),
                  fontSize: 14,
                  marginTop: 4,
                  textAlign: 'center'
                }}>
                  {currentFolderId
                    ? t('This folder is empty. Upload documents or create subfolders to organize your files.')
                    : t('Upload your first document to get started. AI will automatically generate titles, descriptions, and summaries.')
                  }
                </Text>
                <XStack gap={12} marginTop={16} flexWrap="wrap" justifyContent="center">
                  <Pressable
                    onPress={() => setShowCreateFolderModal(true)}
                    style={[
                      getHeroButtonStyle(theme, 'outline'),
                      {
                        paddingHorizontal: 20,
                        paddingVertical: 10,
                        borderRadius: 8,
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                      }
                    ]}
                  >
                    <MaterialIcons name="create-new-folder" size={16} color={HERO_PRIMARY_COLOR} />
                    <Text style={{ color: HERO_PRIMARY_COLOR, fontWeight: '600', fontSize: 14 }}>
                      {t('Create Folder')}
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={handleUploadDocument}
                    style={[
                      getHeroButtonStyle(theme, 'solid'),
                      {
                        paddingHorizontal: 20,
                        paddingVertical: 10,
                        borderRadius: 8,
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                      }
                    ]}
                  >
                    <Feather name="upload" size={16} color={HERO_TEXT_COLOR} />
                    <Text style={{ color: HERO_TEXT_COLOR, fontWeight: '600', fontSize: 14 }}>
                      {t('Upload Document')}
                    </Text>
                  </Pressable>
                </XStack>
              </View>
            </YStack>
          )}

          {/* Items Grid */}
          {!isLoading && !foldersLoading && filteredItems.length > 0 && (
            <View style={{ flex: 1 }}>
              <FlatList
                data={filteredItems}
                renderItem={renderItem}
                numColumns={1}
                contentContainerStyle={{
                  padding: 16,
                  paddingBottom: 32,
                }}
                style={{ flex: 1 }}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                initialNumToRender={10}
                maxToRenderPerBatch={10}
                windowSize={5}
                removeClippedSubviews={Platform.OS === 'android'}
                keyExtractor={(item) => item.id}
                refreshing={refreshing}
                onRefresh={onRefresh}
              />
            </View>
          )}
        </YStack>
      </Animatable.View>

      {/* Create Folder Modal */}
      <Modal
        visible={showCreateFolderModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowCreateFolderModal(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}>
          <Animatable.View
            animation="zoomIn"
            duration={300}
            style={{
              backgroundColor: theme ? '#ffffff' : '#1e293b',
              borderRadius: 16,
              padding: 24,
              width: '100%',
              maxWidth: 400,
              borderWidth: 1,
              borderColor: theme ? '#e2e8f0' : '#334155',
            }}
          >
            <YStack gap={16}>
              <XStack alignItems="center" justifyContent="space-between" marginBottom={8}>
                <Title style={{ color: getModernTextColor(theme), fontSize: 20, fontWeight: '700' }}>
                  {t('Create New Folder')}
                </Title>
                <Pressable
                  onPress={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                  }}
                  style={{
                    padding: 4,
                  }}
                >
                  <AntDesign name="close" size={20} color={getModernTextColor(theme, 'muted')} />
                </Pressable>
              </XStack>

              <YStack gap={8}>
                <Text style={{ color: getModernTextColor(theme), fontSize: 14, fontWeight: '500' }}>
                  {t('Folder Name')}
                </Text>
                <TextInput
                  value={newFolderName}
                  onChangeText={setNewFolderName}
                  placeholder={t('Enter folder name...')}
                  placeholderTextColor={theme ? '#64748b' : '#94a3b8'}
                  style={{
                    backgroundColor: theme ? '#f8fafc' : '#334155',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    borderRadius: 8,
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    fontSize: 15,
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#334155',
                  }}
                  autoFocus
                  onSubmitEditing={handleCreateFolder}
                />
              </YStack>

              <XStack gap={12} justifyContent="flex-end" marginTop={8}>
                <Pressable
                  onPress={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                  }}
                  style={[
                    getHeroButtonStyle(theme, 'outline'),
                    {
                      paddingHorizontal: 20,
                      paddingVertical: 10,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  ]}
                >
                  <Text style={{ color: getModernTextColor(theme), fontWeight: '600' }}>
                    {t('Cancel')}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={handleCreateFolder}
                  disabled={createFolderMutation.isPending}
                  style={[
                    getHeroButtonStyle(theme, 'solid'),
                    {
                      paddingHorizontal: 20,
                      paddingVertical: 10,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: createFolderMutation.isPending ? 0.6 : 1,
                    }
                  ]}
                >
                  {createFolderMutation.isPending ? (
                    <ActivityIndicator size="small" color={HERO_TEXT_COLOR} />
                  ) : (
                    <Text style={{ color: HERO_TEXT_COLOR, fontWeight: '600' }}>
                      {t('Create')}
                    </Text>
                  )}
                </Pressable>
              </XStack>
            </YStack>
          </Animatable.View>
        </View>
      </Modal>

      {/* Move Document Modal */}
      <Modal
        visible={showMoveModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowMoveModal(false);
          setDocumentToMove(null);
        }}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}>
          <Animatable.View
            animation="zoomIn"
            duration={300}
            style={{
              backgroundColor: theme ? '#ffffff' : '#1e293b',
              borderRadius: 16,
              padding: 24,
              width: '100%',
              maxWidth: 400,
              maxHeight: '80%',
              borderWidth: 1,
              borderColor: theme ? '#e2e8f0' : '#334155',
            }}
          >
            <YStack gap={16} flex={1}>
              <XStack alignItems="center" justifyContent="space-between" marginBottom={8}>
                <Title style={{ color: getModernTextColor(theme), fontSize: 20, fontWeight: '700' }}>
                  {t('Move Document')}
                </Title>
                <Pressable
                  onPress={() => {
                    setShowMoveModal(false);
                    setDocumentToMove(null);
                  }}
                  style={{
                    padding: 4,
                  }}
                >
                  <AntDesign name="close" size={20} color={getModernTextColor(theme, 'muted')} />
                </Pressable>
              </XStack>

              {documentToMove && (
                <Text style={{ color: getModernTextColor(theme, 'muted'), fontSize: 14 }}>
                  {t('Move')} "{documentToMove.name}" {t('to')}:
                </Text>
              )}

              <ScrollView style={{ maxHeight: 300 }}>
                <YStack gap={8}>
                  <Pressable
                    onPress={() => handleMoveToFolder(null)}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: theme ? '#f8fafc' : '#334155',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#334155',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <MaterialIcons name="home" size={20} color={getModernTextColor(theme)} />
                    <Text style={{ color: getModernTextColor(theme), fontSize: 15, fontWeight: '500' }}>
                      {t('Root Folder')}
                    </Text>
                  </Pressable>

                  {Array.isArray(folders) && folders.map((folder) => (
                    <Pressable
                      key={folder.id}
                      onPress={() => handleMoveToFolder(folder.id)}
                      style={{
                        padding: 12,
                        borderRadius: 8,
                        backgroundColor: theme ? '#f8fafc' : '#334155',
                        borderWidth: 1,
                        borderColor: theme ? '#e2e8f0' : '#334155',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <MaterialIcons name="folder" size={20} color="#5CC0EB" />
                      <Text style={{ color: getModernTextColor(theme), fontSize: 15, fontWeight: '500' }}>
                        {folder.name}
                      </Text>
                    </Pressable>
                  ))}
                </YStack>
              </ScrollView>

              <XStack gap={12} justifyContent="flex-end" marginTop={8}>
                <Pressable
                  onPress={() => {
                    setShowMoveModal(false);
                    setDocumentToMove(null);
                  }}
                  style={[
                    getHeroButtonStyle(theme, 'outline'),
                    {
                      paddingHorizontal: 20,
                      paddingVertical: 10,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  ]}
                >
                  <Text style={{ color: getModernTextColor(theme), fontWeight: '600' }}>
                    {t('Cancel')}
                  </Text>
                </Pressable>
              </XStack>
            </YStack>
          </Animatable.View>
        </View>
      </Modal>
    </DefaultScreen>
  );
};

const stylesLocal = StyleSheet.create({
  documentItemContainer: {
    flex: 1,
    width: '100%',
  },
  documentThumbnail: {
    width: 120,
    resizeMode: 'cover',
    height: 160,
    borderRadius: 8,
    minHeight: 160,
    maxHeight: 160,
  },
  documentPlaceholder: {
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    height: 160,
    minHeight: 160,
    maxHeight: 160,
    width: 120,
  },
});

export default UserUploadedDocumentsScreen;

